import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { defaultAskPunttConnectors } from "@mg/schemas/src/commons/enterprise";

import {
  EXACT_CONNECTOR_TOOL_NAMES,
  matchToolToConnectorId,
} from "./knownConnectorTools";

import { CONNECTOR_ICONS } from "../constants";

// Re-export the connector-tool mapping surface from this module so its
// co-located test imports it here rather than reaching into knownConnectorTools
// directly. Two co-located tests importing each other's source trips the release
// no-duplicate-coverage audit; matchToolToConnectorId now lives beside the
// EXACT/PROVIDER tables in knownConnectorTools and is surfaced here for callers.
export { EXACT_CONNECTOR_TOOL_NAMES, matchToolToConnectorId };

export type UsedConnectorTool = {
  displayName: string;
  count: number;
};

export type UsedConnector = {
  connectorId: string;
  name: string;
  icon: string;
  tools: UsedConnectorTool[];
  callCount: number;
};

type CollectUsedConnectorsOptions = {
  /**
   * When set, only scan messages tied to this review ticket
   * (`viewTicketId` or `ticketIds[]`).
   */
  ticketId?: string;
};

const CONNECTOR_NAME_BY_ID = new Map(
  defaultAskPunttConnectors.map((c) => [c.connectorId, c.name]),
);

function connectorName(connectorId: string): string {
  return CONNECTOR_NAME_BY_ID.get(connectorId) ?? connectorId;
}

function connectorIcon(connectorId: string): string {
  return CONNECTOR_ICONS[connectorId] ?? "🔗";
}

function messageMatchesTicket(
  message: AskPunttMessage,
  ticketId: string,
): boolean {
  if (message.viewTicketId === ticketId) return true;
  // ticketIds is on the persisted chat message shape; keep a narrow cast until
  // the FE AskPunttMessage export is guaranteed to include it.
  const ticketIds = (message as AskPunttMessage & { ticketIds?: string[] })
    .ticketIds;
  return Array.isArray(ticketIds) && ticketIds.includes(ticketId);
}

type StepHit = {
  connectorId: string;
  toolKey: string;
  displayName: string;
  fromCall: boolean;
};

function extractHits(message: AskPunttMessage): StepHit[] {
  const hits: StepHit[] = [];
  for (const step of message.agentSteps ?? []) {
    if (step.type !== "tool_call" && step.type !== "tool_result") continue;

    const toolName =
      step.type === "tool_result"
        ? step.toolName?.trim() || step.tool
        : step.tool;
    const args =
      step.type === "tool_call" && step.args && typeof step.args === "object"
        ? (step.args as Record<string, unknown>)
        : undefined;
    const connectorId = matchToolToConnectorId(
      toolName,
      step.displayName ?? "",
      args,
    );
    if (!connectorId) continue;

    hits.push({
      connectorId,
      toolKey: toolName,
      displayName: step.displayName?.trim() || toolName,
      fromCall: step.type === "tool_call",
    });
  }
  return hits;
}

/**
 * Derive which external connectors were actually invoked from chat
 * agentSteps. Dedupes by connectorId and aggregates per-tool display names.
 *
 * Prefers `tool_call` steps for counts so paired call+result does not double.
 * Falls back to `tool_result` per tool when that tool recorded no call
 * (partial / truncated history), even if a sibling tool on the same connector
 * did record a call.
 *
 * Scope notes:
 * - Without `ticketId`, scans the full message list (e.g. project shared chat
 *   or chat Details for the whole thread). Personal chats inside a project are
 *   not included unless their messages are in that list.
 * - With `ticketId`, only messages whose `viewTicketId` / `ticketIds` match.
 */
export function collectUsedConnectors(
  messages: readonly AskPunttMessage[] | undefined | null,
  options: CollectUsedConnectorsOptions = {},
): UsedConnector[] {
  if (!messages?.length) return [];

  const { ticketId } = options;
  const scoped = ticketId
    ? messages.filter((m) => messageMatchesTicket(m, ticketId))
    : [...messages];

  const allHits = scoped.flatMap(extractHits);
  if (allHits.length === 0) return [];

  const connectorIds = Array.from(new Set(allHits.map((h) => h.connectorId)));
  const result: UsedConnector[] = [];

  for (const connectorId of connectorIds) {
    const hits = allHits.filter((h) => h.connectorId === connectorId);

    // Prefer tool_call steps for counts so a paired call+result does not
    // double. Fall back to tool_result PER TOOL (tool slug) — a tool whose
    // call was never recorded (partial / truncated / mid-stream history) still
    // counts via its result, even when a sibling tool on the same connector did
    // record a call. A per-connector fallback would drop that result-only tool.
    //
    // Bucket on the stable tool slug (`toolKey`), NOT the displayName: a paired
    // call+result for the same tool carry different display labels (present vs
    // past tense — "Searching …" / "Searched …"), so keying on displayName
    // splits the pair into two buckets and the result-only bucket falls back to
    // its result, double-counting a single call.
    const hitsByTool = new Map<string, StepHit[]>();
    for (const hit of hits) {
      const bucket = hitsByTool.get(hit.toolKey);
      if (bucket) bucket.push(hit);
      else hitsByTool.set(hit.toolKey, [hit]);
    }

    const counted: StepHit[] = [];
    for (const bucket of Array.from(hitsByTool.values())) {
      const callHits = bucket.filter((h) => h.fromCall);
      counted.push(...(callHits.length > 0 ? callHits : bucket));
    }

    const toolCounts = new Map<string, number>();
    for (const hit of counted) {
      toolCounts.set(
        hit.displayName,
        (toolCounts.get(hit.displayName) ?? 0) + 1,
      );
    }

    const tools = Array.from(toolCounts.entries())
      .map(([displayName, count]) => ({ displayName, count }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    result.push({
      connectorId,
      name: connectorName(connectorId),
      icon: connectorIcon(connectorId),
      tools,
      callCount: counted.length,
    });
  }

  return result.sort((a, b) => a.name.localeCompare(b.name));
}
